import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import StableMuiDialog from '../../../../common/StableMuiDialog.js';
import DialogContent from '@mui/material/DialogContent';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import { axiosInstanceNoToast } from '../../../../fetchConfig/axiosConfig.js';
import { toast } from '../../../../common/toast.js';
import { enrichClientCases, orderEnrichedCasesByUids, syncCaseNotesFromCases } from '../../../../services/initializeFetchUtils.js';
import { usePersistedDataStore } from '../../../store.js';
import { useUser } from '../../../UserContext.js';
import { getWorkupS3FileName } from '../../../../common/mappings.js';
import {
  displayFileLabel,
  formatNyscefLastModifiedEst,
  getNyscefRawFilename,
  groupNyscefExhibits,
  humanizeUnderscoreSegment,
  nyscefLastModifiedTime,
} from '../../NyscefUpload/nyscefExhibitUtils.js';

/**
 * Extracts the filename from a file object
 */
const getFilename = (file) => {
  if (!file) return '';
  const raw = file.Filename ?? file.filename ?? (file.Key ? file.Key.split('/').pop() : '') ?? '';
  return String(raw).toLowerCase();
};

const isFilingFile = (file) => getFilename(file).includes('filing');

const formatFilingLabel = (filename) => {
  const base = String(filename).trim().replace(/\.pdf$/i, '').replace(/-[a-f0-9]{8}$/i, '').toLowerCase();
  if (!base) return '';
  if (base === 'villagebarfiling' || base === 'village_bar_filing') return 'Village BAR Filing';
  if (base === 'barfiling' || base === 'bar_filing') return 'BAR Filing';
  if (base.includes('village') && base.includes('filing')) return 'Village BAR Filing';
  if (base.includes('bar') && base.includes('filing')) return 'BAR Filing';
  return humanizeUnderscoreSegment(base) || base;
};

const displayFilingLabel = (file, index) => {
  const s = String(getNyscefRawFilename(file)).trim();
  if (!s) return `Filing ${index + 1}`;
  return formatFilingLabel(s) || s;
};

const compareFilesByLastModified = (a, b) => {
  const ta = nyscefLastModifiedTime(a);
  const tb = nyscefLastModifiedTime(b);
  if (ta == null && tb == null) {
    return String(a.Key || '').localeCompare(String(b.Key || ''));
  }
  if (ta == null) return 1;
  if (tb == null) return -1;
  if (ta !== tb) return ta - tb;
  return String(a.Key || '').localeCompare(String(b.Key || ''));
};

const sortFilesByLastModified = (list) => [...list].sort(compareFilesByLastModified);

const workupPickerColors = {
  bgPrimary: '#ffffff',
  textPrimary: '#1a1a18',
  textSecondary: '#5f5e5a',
  textTertiary: '#888780',
  borderTertiary: 'rgba(0,0,0,0.12)',
  borderSecondary: 'rgba(0,0,0,0.22)',
  accent: '#185FA5',
  accentBg: '#E6F1FB',
};

/** Fallback before the first row is measured: two compact exhibit cards. */
const NYSCEF_EXHIBIT_LIST_DEFAULT_HEIGHT = 82;
const NYSCEF_EXHIBIT_LIST_MIN_HEIGHT = 40;

const NYSCEF_PARTY_STYLES = {
  aventine: {
    border: '#185FA5',
    bg: '#F3F8FD',
    labelColor: '#185FA5',
    fadeTo: '#F3F8FD',
    scrollHint: '#185FA5',
  },
  town: {
    border: '#E8A8A8',
    bg: '#FDF6F6',
    labelColor: '#B85454',
    fadeTo: '#FDF6F6',
    scrollHint: '#B85454',
  },
  other: {
    border: workupPickerColors.borderTertiary,
    bg: workupPickerColors.bgPrimary,
    labelColor: workupPickerColors.textTertiary,
    fadeTo: workupPickerColors.bgPrimary,
    scrollHint: workupPickerColors.textTertiary,
  },
};

const INTERNAL_WORKUP_LABELS = {
  onePager: 'One Pager',
  fnma: 'FNMA',
  equity: 'Assessment Equity',
};

const TOWN_WORKUP_LABELS = {
  onePager: 'Sales Comps',
  fnma: 'Sales Comps',
  equity: 'Assessment Equity',
};

const workupVariantLabel = (tabKind, isTownView) =>
  (isTownView ? TOWN_WORKUP_LABELS : INTERNAL_WORKUP_LABELS)[tabKind] ??
  INTERNAL_WORKUP_LABELS[tabKind];

/** Hide nav pane; default 100% zoom (best-effort in Chrome/Edge built-in PDF viewer). */
const pdfViewerSrc = (url) => {
  const u = String(url || '').trim().replace(/'/g, '%27');
  if (!u || u.includes('#')) return u;
  return `${u}#navpanes=0&pagemode=none&zoom=100`;
};

const DocumentPickerCard = ({ active, disabled, label, onClick, showFullLabel = false, compact = false }) => (
  <Box
    onClick={disabled ? undefined : onClick}
    role={disabled ? undefined : 'button'}
    tabIndex={disabled ? undefined : 0}
    onKeyDown={
      disabled
        ? undefined
        : (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          }
    }
    sx={{
      display: 'flex',
      alignItems: showFullLabel ? 'flex-start' : 'center',
      gap: 1.25,
      border: '1.5px solid',
      borderColor: active ? workupPickerColors.accent : workupPickerColors.borderTertiary,
      borderRadius: '8px',
      p: compact ? '8px 12px' : '10px 14px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'border-color 0.15s, background 0.15s',
      background: active ? workupPickerColors.accentBg : workupPickerColors.bgPrimary,
      opacity: disabled ? 0.55 : 1,
      width: '100%',
      '&:hover': disabled
        ? {}
        : { borderColor: active ? workupPickerColors.accent : workupPickerColors.borderSecondary },
    }}
  >
    <PictureAsPdfOutlinedIcon
      sx={{
        fontSize: 18,
        color: active ? workupPickerColors.accent : workupPickerColors.textSecondary,
        flexShrink: 0,
      }}
    />
    <Typography
      sx={{
        fontSize: 13,
        fontWeight: 500,
        color: workupPickerColors.textPrimary,
        m: 0,
        flex: 1,
        ...(showFullLabel
          ? { whiteSpace: 'normal', lineHeight: 1.35, wordBreak: 'break-word' }
          : {
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }),
      }}
    >
      {label}
    </Typography>
  </Box>
);

const NyscefSectionLabel = ({ children, color }) => (
  <Typography
    component="p"
    sx={{
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: color || workupPickerColors.textTertiary,
      m: '0 0 6px',
    }}
  >
    {children}
  </Typography>
);

const NyscefScrollableExhibitList = ({ entries, partyStyle, selectedIndex, onSelect, listMaxHeight }) => {
  const scrollRef = useRef(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);
  const [hiddenCount, setHiddenCount] = useState(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const update = () => {
      const overflow = el.scrollHeight > el.clientHeight + 2;
      const notAtBottom = el.scrollTop + el.clientHeight < el.scrollHeight - 2;
      setHasMoreBelow(overflow && notAtBottom);

      const viewBottom = el.getBoundingClientRect().bottom;
      let remaining = 0;
      for (const child of el.children) {
        if (child.getBoundingClientRect().top >= viewBottom - 2) remaining += 1;
      }
      setHiddenCount(remaining);
    };

    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [entries, listMaxHeight]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        ref={scrollRef}
        data-nyscef-list
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          maxHeight: entries.length > 1 ? listMaxHeight : 'none',
          overflowY: 'auto',
          pr: 0.25,
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: 3,
            backgroundColor: 'rgba(0,0,0,0.18)',
          },
        }}
      >
        {entries.map(({ file, listIndex, groupLabel, fullLabel }, i) => {
          const modifiedEst = formatNyscefLastModifiedEst(file);
          const numberedLabel = `${i + 1}. ${groupLabel}`;
          const tooltipTitle = modifiedEst ? `${modifiedEst}\n${fullLabel}` : fullLabel;
          return (
            <Tooltip
              key={file.Key || file.Url || listIndex}
              title={tooltipTitle}
              placement="top"
              enterDelay={400}
              disableInteractive
              slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line', pointerEvents: 'none' } } }}
            >
              <Box data-nyscef-row>
                <DocumentPickerCard
                  active={selectedIndex === listIndex}
                  disabled={false}
                  label={numberedLabel}
                  showFullLabel
                  compact
                  onClick={() => onSelect(listIndex)}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      {entries.length > 1 && hasMoreBelow && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 32,
            pointerEvents: 'none',
            background: `linear-gradient(180deg, transparent 0%, ${partyStyle.fadeTo} 72%)`,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            pb: 0.25,
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: partyStyle.scrollHint,
              m: 0,
              opacity: 0.9,
            }}
          >
            {hiddenCount > 0 ? `${hiddenCount} more — scroll` : 'Scroll for more'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const NyscefPartySection = ({ partyKey, title, entries, selectedIndex, onSelect, listMaxHeight }) => {
  const partyStyle = NYSCEF_PARTY_STYLES[partyKey];
  if (!entries.length || !partyStyle) return null;

  return (
    <Box
      sx={{
        flex: '1 1 320px',
        minWidth: 280,
        maxWidth: '100%',
        border: '2px solid',
        borderColor: partyStyle.border,
        borderRadius: '10px',
        bgcolor: partyStyle.bg,
        p: '8px 10px',
      }}
    >
      <NyscefSectionLabel color={partyStyle.labelColor}>
        {title} ({entries.length})
      </NyscefSectionLabel>
      <NyscefScrollableExhibitList
        entries={entries}
        partyStyle={partyStyle}
        selectedIndex={selectedIndex}
        onSelect={onSelect}
        listMaxHeight={listMaxHeight}
      />
    </Box>
  );
};

const measureExhibitListHeight = (root, groups) => {
  const lists = [...(root?.querySelectorAll('[data-nyscef-list]') || [])];
  if (!lists.length) return 0;
  const showTwo = [groups.aventine, groups.town, groups.other].some((entries) => entries.length > 1);
  const savedMax = lists.map((list) => list.style.maxHeight);
  lists.forEach((list) => { list.style.maxHeight = 'none'; });
  let height = 0;
  for (const list of lists) {
    const rows = list.querySelectorAll('[data-nyscef-row]');
    if (!rows.length) continue;
    const visible = rows.length === 1 ? 1 : (showTwo ? 2 : 1);
    if (visible > rows.length) continue;
    const span = rows[visible - 1].getBoundingClientRect().bottom - rows[0].getBoundingClientRect().top;
    if (span > 16) height = Math.max(height, Math.ceil(span));
  }
  lists.forEach((list, i) => { list.style.maxHeight = savedMax[i]; });
  return height;
};

const NyscefExhibitPicker = ({ groups, selectedIndex, onSelect }) => {
  const pickerRef = useRef(null);
  const defaultHeightRef = useRef(NYSCEF_EXHIBIT_LIST_DEFAULT_HEIGHT);
  const sizedRef = useRef(false);
  const [listHeight, setListHeight] = useState(NYSCEF_EXHIBIT_LIST_DEFAULT_HEIGHT);
  const [isResizing, setIsResizing] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useLayoutEffect(() => {
    if (sizedRef.current || collapsed) return;
    const next = measureExhibitListHeight(pickerRef.current, groups);
    if (!next) return;
    defaultHeightRef.current = next;
    setListHeight(next);
    sizedRef.current = true;
  }, [groups, collapsed]);

  const handleResizeStart = useCallback((e) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    const startY = e.clientY;
    const startH = listHeight;
    const rowEl = pickerRef.current?.querySelector('[data-nyscef-row]');
    const rowH = Math.max(
      NYSCEF_EXHIBIT_LIST_MIN_HEIGHT,
      Math.ceil(rowEl?.getBoundingClientRect().height || 0)
    );
    const maxH = Math.max(rowH, Math.round(window.innerHeight * 0.45));
    let latest = startH;
    let ended = false;
    setIsResizing(true);

    const endDrag = () => {
      if (ended) return;
      ended = true;
      setIsResizing(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    const onMove = (moveEvent) => {
      const raw = startH + (moveEvent.clientY - startY);
      if (raw <= 0) {
        endDrag();
        setListHeight(defaultHeightRef.current);
        setCollapsed(true);
        return;
      }
      latest = Math.min(maxH, raw);
      setListHeight(latest);
    };
    const onEnd = () => {
      endDrag();
      setListHeight(latest);
    };
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
  }, [listHeight]);

  const evidenceCount = groups.aventine.length + groups.town.length + groups.other.length;
  const toggleEvidence = () => {
    setListHeight(defaultHeightRef.current);
    setCollapsed((open) => !open);
  };

  return (
    <Box
      sx={{
        flexShrink: 0,
        borderBottom: collapsed ? 0 : 1,
        borderColor: 'divider',
        bgcolor: workupPickerColors.bgPrimary,
      }}
    >
      <Divider sx={{ my: 0.5, px: 2 }}>
        <Chip
          size="small"
          variant="outlined"
          clickable
          onClick={toggleEvidence}
          aria-expanded={!collapsed}
          icon={(
            <ExpandMoreIcon
              sx={{ transition: 'transform .2s', transform: collapsed ? 'none' : 'rotate(180deg)' }}
            />
          )}
          sx={{
            borderRadius: '999px',
            height: 26,
            fontSize: 12,
            borderWidth: '0.5px',
            bgcolor: 'background.paper',
            '&:hover': { bgcolor: 'action.hover' },
            '& .MuiChip-label': {
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              pl: '4px',
              pr: '6px',
            },
            '& .MuiChip-icon': { fontSize: 14, ml: '6px', mr: 0 },
          }}
          label={(
            <>
              {collapsed ? 'Show evidence' : 'Hide evidence'}
              <Box
                component="span"
                aria-hidden
                sx={{
                  bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(57, 135, 229, 0.28)' : '#E6F1FB'),
                  color: (theme) => (theme.palette.mode === 'dark' ? '#9ec5f4' : '#185FA5'),
                  borderRadius: '999px',
                  px: '6px',
                  minWidth: 16,
                  fontSize: 11,
                  lineHeight: '16px',
                  fontWeight: 600,
                  textAlign: 'center',
                }}
              >
                {evidenceCount}
              </Box>
            </>
          )}
        />
      </Divider>
      {!collapsed && (
        <Box ref={pickerRef} sx={{ px: 2, pt: 0.5, pb: 0.5 }}>
          {isResizing && (
            <Box
              aria-hidden
              sx={{ position: 'fixed', inset: 0, zIndex: 2000, cursor: 'ns-resize' }}
            />
          )}
          <Box
            role="group"
            aria-label="NYSCEF documents"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              width: '100%',
              maxWidth: 1200,
              mx: 'auto',
              justifyContent: 'center',
            }}
          >
            <NyscefPartySection
              partyKey="aventine"
              title="Aventine"
              entries={groups.aventine}
              selectedIndex={selectedIndex}
              onSelect={onSelect}
              listMaxHeight={listHeight}
            />
            <NyscefPartySection
              partyKey="town"
              title="Town"
              entries={groups.town}
              selectedIndex={selectedIndex}
              onSelect={onSelect}
              listMaxHeight={listHeight}
            />
            <NyscefPartySection
              partyKey="other"
              title="Other"
              entries={groups.other}
              selectedIndex={selectedIndex}
              onSelect={onSelect}
              listMaxHeight={listHeight}
            />
          </Box>
          <Box
            role="separator"
            aria-orientation="horizontal"
            aria-label="Resize document list"
            onMouseDown={handleResizeStart}
            sx={{
              height: 6,
              mt: 0.75,
              mb: 0.25,
              mx: 'auto',
              maxWidth: 1200,
              cursor: 'ns-resize',
              borderRadius: '4px',
              bgcolor: isResizing ? workupPickerColors.accent : 'rgba(0,0,0,0.12)',
              '&:hover': { bgcolor: workupPickerColors.accent },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

const WorkupVariantPickerRow = ({ variants, workupVariantKind, onSelect, compact = false }) => (
  <Box
    role="group"
    aria-label="workup type"
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: compact ? 0.75 : 1,
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {variants.map((variant) => {
      const active = workupVariantKind === variant.tabKind;
      const card = (
        <DocumentPickerCard
          active={active}
          disabled={!variant.enabled}
          label={variant.label}
          onClick={() => onSelect(variant.tabKind)}
          compact={compact}
        />
      );
      return variant.enabled ? (
        <Box
          key={variant.tabKind}
          sx={{ flex: compact ? '0 1 auto' : '1 1 140px', minWidth: compact ? 120 : 140, maxWidth: compact ? 200 : 220 }}
        >
          {card}
        </Box>
      ) : (
        <Tooltip key={variant.tabKind} title="Not Generated" placement="top">
          <Box
            sx={{ flex: compact ? '0 1 auto' : '1 1 140px', minWidth: compact ? 120 : 140, maxWidth: compact ? 200 : 220 }}
          >
            {card}
          </Box>
        </Tooltip>
      );
    })}
  </Box>
);

const DocumentPickerStrip = ({ ariaLabel, maxWidth = 720, children }) => (
  <Box
    sx={{
      px: 2,
      py: 1.25,
      flexShrink: 0,
      borderBottom: 1,
      borderColor: 'divider',
      display: 'flex',
      justifyContent: 'center',
      bgcolor: workupPickerColors.bgPrimary,
    }}
  >
    <Box
      role="group"
      aria-label={ariaLabel}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 1,
        width: '100%',
        maxWidth,
      }}
    >
      {children}
    </Box>
  </Box>
);

const FilingsDocumentPicker = ({ filingsList, selectedIndex, onSelect }) => (
  <DocumentPickerStrip ariaLabel="Filings" maxWidth={960}>
    {filingsList.map((f, i) => {
      const label = displayFilingLabel(f, i);
      const modifiedEst = formatNyscefLastModifiedEst(f);
      const tooltipTitle = modifiedEst ? `${modifiedEst}\n${label}` : label;
      return (
        <Tooltip
          key={f.Key || f.Url || i}
          title={tooltipTitle}
          placement="top"
          enterDelay={400}
          disableInteractive
          slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line', pointerEvents: 'none' } } }}
        >
          <Box sx={{ flex: '1 1 200px', minWidth: 180, maxWidth: 400 }}>
            <DocumentPickerCard
              active={selectedIndex === i}
              disabled={false}
              label={label}
              showFullLabel
              compact
              onClick={() => onSelect(i)}
            />
          </Box>
        </Tooltip>
      );
    })}
  </DocumentPickerStrip>
);

const pdfFrameStyle = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  border: 'none',
};

const PdfViewer = React.memo(({ url, title }) => {
  const urlRef = useRef(url);
  urlRef.current = url;
  const [readyUrl, setReadyUrl] = useState(url);
  const frameUrls = !url ? [] : url === readyUrl ? [readyUrl] : [readyUrl, url].filter(Boolean);

  return frameUrls.map((frameUrl) => {
    const visible = frameUrl === readyUrl;
    return (
      <iframe
        key={frameUrl}
        src={pdfViewerSrc(frameUrl)}
        title={title}
        onLoad={visible ? undefined : () => {
          if (urlRef.current === frameUrl) setReadyUrl(frameUrl);
        }}
        style={{
          ...pdfFrameStyle,
          opacity: visible ? 1 : 0,
          pointerEvents: visible ? 'auto' : 'none',
        }}
      />
    );
  });
});

/**
 * WorkupViewerDialog - A reusable dialog component for viewing workup PDFs
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {Function} onClose - Callback when dialog is closed
 * @param {Array} files - Array of S3 file objects with Url, Filename, and/or Key properties
 * @param {Array} [nyscefFiles] - NYSCEF intake S3 objects (same shape as files); shown on NYSCEF tab
 * @param {string} [address] - Subject property address shown under the Documents title
 * @param {string} [scarIndexNumber] - SCAR index shown under the Documents title
 * @param {string} initialWorkupType - Optional: 'sales', 'equity', 'assessment', 'Unequal', 'Excessive', 'onepager', or 'fnma' to set initial tab
 *
 * Town view: NYSCEF/Filings hidden; workup type cards inline in header. Town links: showOnePagers
 * Town link: shows Sales Comps or FNMA Comps by permission (even if not generated); file gates viewing only.
 */
const WorkupViewerDialog = ({
  open,
  onClose,
  files = [],
  nyscefFiles = [],
  address = '',
  scarIndexNumber = '',
  initialWorkupType = 'Unequal',
}) => {
  const { user } = useUser();
  const isTownView = Boolean(user?.townView);
  const isTownLink = isTownView && Boolean(user?.isLinkAuthenticated);

  const [activeTopTab, setActiveTopTab] = useState(0);
  const [workupVariantKind, setWorkupVariantKind] = useState('onePager');
  const [nyscefFileIndex, setNyscefFileIndex] = useState(0);
  const [filingsFileIndex, setFilingsFileIndex] = useState(0);
  const [refreshingCases, setRefreshingCases] = useState(false);
  const [refreshedView, setRefreshedView] = useState(null);
  const isFetching = usePersistedDataStore((state) => state.isFetching);
  const prevOpenRef = useRef(false);
  const openInitializedRef = useRef(false);
  const workupViewRecordedRef = useRef(false);

  const refreshKey = `${address}|${scarIndexNumber}`;
  const activeRefresh = refreshedView?.refreshKey === refreshKey ? refreshedView : null;
  const viewedFiles = activeRefresh?.files ?? files;
  const viewedNyscefFiles = activeRefresh?.nyscefFiles ?? nyscefFiles;
  const viewedAddress = activeRefresh?.address ?? address;
  const viewedScarIndex = activeRefresh?.scarIndexNumber ?? scarIndexNumber;

  const workupFiles = useMemo(
    () => (Array.isArray(viewedFiles) ? viewedFiles : []).filter((f) => !isFilingFile(f)),
    [viewedFiles]
  );

  const filingsList = useMemo(() => {
    const withUrl = (Array.isArray(viewedFiles) ? viewedFiles : []).filter((f) => f?.Url && isFilingFile(f));
    return sortFilesByLastModified(withUrl);
  }, [viewedFiles]);

  const nyscefList = useMemo(
    () => (Array.isArray(viewedNyscefFiles) ? viewedNyscefFiles : []).filter((f) => f?.Url),
    [viewedNyscefFiles]
  );

  const nyscefByParty = useMemo(() => groupNyscefExhibits(nyscefList), [nyscefList]);

  const { workupVariants, topTabsConfig } = useMemo(() => {
    const townLinkPrefersOnePagerClosed =
      isTownView && isTownLink && user?.showOnePagers !== false;
    const emptyVariants = [
      {
        label: workupVariantLabel('onePager', isTownView),
        file: null,
        offered: !(isTownView && isTownLink) || townLinkPrefersOnePagerClosed,
        enabled: false,
        tabKind: 'onePager',
      },
      {
        label: workupVariantLabel('fnma', isTownView),
        file: null,
        offered: !(isTownView && isTownLink) || !townLinkPrefersOnePagerClosed,
        enabled: false,
        tabKind: 'fnma',
      },
      {
        label: workupVariantLabel('equity', isTownView),
        file: null,
        offered: true,
        enabled: false,
        tabKind: 'equity',
      },
    ];
    if (!open) {
      return {
        workupVariants: emptyVariants,
        topTabsConfig: [
          { label: 'Workup', enabled: false, tabKind: 'workup', isWorkup: true },
          { label: 'Filings', enabled: false, tabKind: 'filings', isFilings: true },
          { label: 'NYSCEF', enabled: false, tabKind: 'nyscef', isNyscef: true },
        ],
      };
    }

    const negotiationObj = usePersistedDataStore.getState().negotiationObj;
    const isVillageMode = negotiationObj?.villageRun === 1;
    const salesReportType = isVillageMode ? 'village' : 'standard';
    const onePagerFile = workupFiles.find((file) => getFilename(file) === `${getWorkupS3FileName(salesReportType, 'onepagermaps')}.pdf`) || null;
    const fnmaFile = workupFiles.find((file) => getFilename(file) === `${getWorkupS3FileName(salesReportType, 'fnmaSummary')}.pdf`) || null;
    const equityFile = workupFiles.find((file) => getFilename(file) === `${getWorkupS3FileName('assessment', 'onepagermaps')}.pdf`) || null;

    const equityEnabled = !!equityFile;
    const townLinkPrefersOnePager = isTownView && isTownLink && user?.showOnePagers !== false;
    let onePagerOffered = true;
    let fnmaOffered = true;
    let onePagerEnabled = false;
    let fnmaEnabled = false;

    // Town link: offer exactly one sales workup type by permission; file presence only gates viewing.
    if (isTownView && isTownLink) {
      onePagerOffered = townLinkPrefersOnePager;
      fnmaOffered = !townLinkPrefersOnePager;
      onePagerEnabled = townLinkPrefersOnePager && !!onePagerFile;
      fnmaEnabled = !townLinkPrefersOnePager && !!fnmaFile;
    } else {
      const allowOnePager =
        !user?.isLinkAuthenticated || user?.showOnePagers !== false;
      onePagerOffered = allowOnePager;
      fnmaOffered = true;
      onePagerEnabled = allowOnePager && !!onePagerFile;
      fnmaEnabled = !!fnmaFile;
    }

    const variants = [
      {
        label: workupVariantLabel('onePager', isTownView),
        file: onePagerFile,
        offered: onePagerOffered,
        enabled: onePagerEnabled,
        tabKind: 'onePager',
      },
      {
        label: workupVariantLabel('fnma', isTownView),
        file: fnmaFile,
        offered: fnmaOffered,
        enabled: fnmaEnabled,
        tabKind: 'fnma',
      },
      {
        label: workupVariantLabel('equity', isTownView),
        file: equityFile,
        offered: true,
        enabled: equityEnabled,
        tabKind: 'equity',
      },
    ];
    const workupTabEnabled = isTownView && isTownLink
      ? variants.some((v) => v.offered)
      : variants.some((v) => v.enabled);

    const topTabs = [
      { label: 'Workup', enabled: workupTabEnabled, tabKind: 'workup', isWorkup: true },
    ];
    if (!isTownView) {
      topTabs.push({
        label: 'Filings',
        enabled: filingsList.length > 0,
        tabKind: 'filings',
        isFilings: true,
      });
      topTabs.push({
        label: 'NYSCEF',
        enabled: nyscefList.length > 0,
        tabKind: 'nyscef',
        isNyscef: true,
      });
    }

    return { workupVariants: variants, topTabsConfig: topTabs };
  }, [
    workupFiles,
    open,
    filingsList.length,
    nyscefList.length,
    isTownView,
    isTownLink,
    user?.isLinkAuthenticated,
    user?.showOnePagers,
  ]);

  const firstAvailableTopTab = useMemo(
    () => topTabsConfig.findIndex((tab) => tab.enabled),
    [topTabsConfig]
  );

  const filingsTabIndex = useMemo(() => topTabsConfig.findIndex((t) => t.isFilings), [topTabsConfig]);

  const nyscefTabIndex = useMemo(() => topTabsConfig.findIndex((t) => t.isNyscef), [topTabsConfig]);

  const activeWorkupVariant = useMemo(
    () => workupVariants.find((v) => v.tabKind === workupVariantKind) ?? workupVariants[0],
    [workupVariants, workupVariantKind]
  );

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setNyscefFileIndex(0);
      setFilingsFileIndex(0);
      setRefreshedView(null);
    }
    prevOpenRef.current = open;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const current = workupVariants.find((v) => v.tabKind === workupVariantKind);
    if (current?.offered !== false && current?.enabled) return;
    const townLinkPreferred =
      isTownView && isTownLink
        ? workupVariants.find((v) =>
            user?.showOnePagers !== false ? v.tabKind === 'onePager' : v.tabKind === 'fnma'
          )
        : null;
    if (townLinkPreferred?.offered) {
      setWorkupVariantKind(townLinkPreferred.tabKind);
      return;
    }
    const firstEnabled = workupVariants.find((v) => v.offered !== false && v.enabled);
    if (firstEnabled) setWorkupVariantKind(firstEnabled.tabKind);
    else {
      const firstOffered = workupVariants.find((v) => v.offered !== false);
      if (firstOffered) setWorkupVariantKind(firstOffered.tabKind);
    }
  }, [open, workupVariants, workupVariantKind, isTownView, isTownLink, user?.showOnePagers]);

  useEffect(() => {
    if (!open) {
      workupViewRecordedRef.current = false;
      return undefined;
    }
    if (!isTownLink || workupViewRecordedRef.current) return undefined;

    let cancelled = false;
    const timerId = setTimeout(() => {
      if (cancelled || workupViewRecordedRef.current) return;
      workupViewRecordedRef.current = true;
      axiosInstanceNoToast.post('/workup_view').catch(() => {});
    }, 5000);

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [open, isTownLink]);

  useEffect(() => {
    if (nyscefFileIndex >= nyscefList.length) {
      setNyscefFileIndex(nyscefList.length ? nyscefList.length - 1 : 0);
    }
  }, [nyscefList.length, nyscefFileIndex]);

  useEffect(() => {
    if (filingsFileIndex >= filingsList.length) {
      setFilingsFileIndex(filingsList.length ? filingsList.length - 1 : 0);
    }
  }, [filingsList.length, filingsFileIndex]);

  useEffect(() => {
    if (!open) {
      openInitializedRef.current = false;
      return;
    }
    if (openInitializedRef.current) return;
    openInitializedRef.current = true;

    if (isTownView && isTownLink) {
      setWorkupVariantKind(user?.showOnePagers !== false ? 'onePager' : 'fnma');
    } else {
      const isEnabled = (kind) => workupVariants.find((v) => v.tabKind === kind)?.enabled;
      let initialVariant = 'onePager';
      if (
        initialWorkupType === 'equity' ||
        initialWorkupType === 'assessment' ||
        initialWorkupType === 'Excessive'
      ) {
        if (isEnabled('equity')) initialVariant = 'equity';
      } else if (initialWorkupType === 'sales' || initialWorkupType === 'Unequal') {
        if (isEnabled('fnma')) initialVariant = 'fnma';
        else if (isEnabled('onePager')) initialVariant = 'onePager';
      } else if (isEnabled('fnma')) initialVariant = 'fnma';
      else if (isEnabled('onePager')) initialVariant = 'onePager';
      else if (isEnabled('equity')) initialVariant = 'equity';
      else {
        initialVariant = workupVariants.find((v) => v.enabled)?.tabKind ?? 'onePager';
      }
      setWorkupVariantKind(initialVariant);
    }

    const workupAny = topTabsConfig.some((t) => t.isWorkup && t.enabled);
    const filingsEnabled = filingsTabIndex >= 0 && topTabsConfig[filingsTabIndex]?.enabled;
    const nyscefEnabled = nyscefTabIndex >= 0 && topTabsConfig[nyscefTabIndex]?.enabled;

    if (!workupAny && filingsEnabled) {
      setActiveTopTab(filingsTabIndex);
      return;
    }
    if (!workupAny && nyscefEnabled) {
      setActiveTopTab(nyscefTabIndex);
      return;
    }

    const workupTabIdx = topTabsConfig.findIndex((t) => t.isWorkup);
    const targetTop =
      workupTabIdx >= 0 && topTabsConfig[workupTabIdx]?.enabled
        ? workupTabIdx
        : firstAvailableTopTab >= 0
          ? firstAvailableTopTab
          : 0;
    setActiveTopTab(
      topTabsConfig[targetTop]?.enabled ? targetTop : (firstAvailableTopTab >= 0 ? firstAvailableTopTab : 0)
    );
  }, [
    open,
    initialWorkupType,
    topTabsConfig,
    firstAvailableTopTab,
    filingsTabIndex,
    nyscefTabIndex,
    workupVariants,
    isTownView,
    isTownLink,
    user?.showOnePagers,
  ]);

  const handleTopTabChange = (event, newValue) => {
    setActiveTopTab(newValue);
  };

  const activeTab = topTabsConfig[activeTopTab];
  const showDocumentTabs = !isTownView;
  const workupVariantsForPicker = useMemo(
    () => workupVariants.filter((v) => v.offered !== false),
    [workupVariants]
  );
  const showWorkupPickerInToolbar = isTownView && open;
  const showWorkupPickerBelow = !isTownView && activeTab?.isWorkup;
  const showFilingsPicker = activeTab?.isFilings && filingsList.length > 0;
  const showNyscefPicker = activeTab?.isNyscef && nyscefList.length > 0;
  const headerAddress = String(viewedAddress || '').trim();
  const headerScarIndex = String(viewedScarIndex || '').trim();
  const safeFilingsIdx = filingsList.length ? Math.min(filingsFileIndex, filingsList.length - 1) : 0;
  const safeNyscefIdx = nyscefList.length ? Math.min(nyscefFileIndex, nyscefList.length - 1) : 0;
  const activeFilingsFile = filingsList[safeFilingsIdx];
  const activeNyscefFile = nyscefList[safeNyscefIdx];

  const activePdf = useMemo(() => {
    if (activeTab?.isNyscef) {
      if (activeNyscefFile?.Url) {
        return {
          url: activeNyscefFile.Url,
          title: displayFileLabel(activeNyscefFile, safeNyscefIdx),
          emptyMessage: 'No NYSCEF file available',
        };
      }
      return { url: null, emptyMessage: 'No NYSCEF file available' };
    }
    if (activeTab?.isFilings) {
      if (activeFilingsFile?.Url) {
        return {
          url: activeFilingsFile.Url,
          title: displayFilingLabel(activeFilingsFile, safeFilingsIdx),
          emptyMessage: 'No filing available',
        };
      }
      return { url: null, emptyMessage: 'No filing available' };
    }
    if (activeTab?.isWorkup || isTownView) {
      if (activeWorkupVariant?.enabled && activeWorkupVariant?.file?.Url) {
        return {
          url: activeWorkupVariant.file.Url,
          title: activeWorkupVariant.label,
          emptyMessage: 'No workup file available',
        };
      }
      return { url: null, emptyMessage: 'No workup file available' };
    }
    return { url: null, emptyMessage: 'No document available' };
  }, [
    activeTab,
    activeNyscefFile,
    activeFilingsFile,
    activeWorkupVariant,
    isTownView,
    safeNyscefIdx,
    safeFilingsIdx,
  ]);

  const handleOpenInWindow = () => {
    if (!activePdf.url) return;
    window.open(
      pdfViewerSrc(activePdf.url),
      'workupPdf',
      'popup=yes,width=1100,height=900,resizable=yes,scrollbars=yes'
    );
  };

  const handleRefreshCases = async () => {
    if (refreshingCases || isFetching) return;
    const negotiationObj = usePersistedDataStore.getState().negotiationObj;
    const previousCases = negotiationObj?.cases || [];
    if (!negotiationObj?.uids?.length) {
      toast.error('No cases to refresh');
      return;
    }
    const previousOrder = previousCases.map((caseItem) => caseItem?.parcel_id).filter(Boolean).map(String);
    setRefreshingCases(true);
    try {
      const result = await enrichClientCases();
      if (!result?.success) return;

      const store = usePersistedDataStore.getState();
      let cases = store.negotiationObj?.cases || [];
      if (previousOrder.length) {
        cases = orderEnrichedCasesByUids(cases, previousOrder);
        store.setNegotiationObj({ ...store.negotiationObj, cases });
        syncCaseNotesFromCases(cases);
      }
      store.incrementNegotiationObjVersion();

      const scar = String(scarIndexNumber || '').trim();
      const addr = String(address || '').trim().toLowerCase();
      const match =
        (scar && cases.find((c) => String(c?.SCARIndexNumber || '').trim() === scar))
        || (addr && cases.find((c) => String(c?.Address || '').trim().toLowerCase() === addr))
        || (cases.length === 1 ? cases[0] : null);
      if (match) {
        setRefreshedView({
          refreshKey,
          files: Array.isArray(match.S3Files) ? match.S3Files : [],
          nyscefFiles: Array.isArray(match.NyscefS3Files) ? match.NyscefS3Files : [],
          address: match.Address || '',
          scarIndexNumber: match.SCARIndexNumber || '',
        });
      }
      toast.success('Case info refreshed');
    } finally {
      setRefreshingCases(false);
    }
  };

  return (
    <StableMuiDialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: 'calc(100vh - 24px)',
          maxHeight: 'calc(100vh - 24px)',
          margin: '12px 32px',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <AppBar position="static" sx={{ backgroundColor: 'background.paper', color: 'text.primary', boxShadow: 1, flexShrink: 0 }}>
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            minHeight: '48px !important',
            flexWrap: isTownView ? 'wrap' : 'nowrap',
            py: isTownView ? 0.75 : 0,
          }}
        >
          <Box sx={{ flexShrink: 0, minWidth: 0, maxWidth: 240 }}>
            <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>
              Documents
            </Typography>
            {headerAddress ? (
              <Typography
                sx={{
                  fontSize: 11,
                  lineHeight: 1.25,
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={headerAddress}
              >
                {headerAddress}
              </Typography>
            ) : null}
            {headerScarIndex ? (
              <Typography sx={{ fontSize: 11, lineHeight: 1.25, color: 'text.secondary' }}>
                {headerScarIndex}
              </Typography>
            ) : null}
          </Box>
          {showWorkupPickerInToolbar && (
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0, px: 0.5 }}>
              <WorkupVariantPickerRow
                variants={workupVariantsForPicker}
                workupVariantKind={workupVariantKind}
                onSelect={setWorkupVariantKind}
                compact
              />
            </Box>
          )}
          {showDocumentTabs && (
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <Tabs
                value={activeTopTab}
                onChange={handleTopTabChange}
                aria-label="document tabs"
                sx={{
                  minHeight: '36px',
                  '& .MuiTab-root': {
                    minHeight: '36px',
                    fontSize: '0.875rem',
                    textTransform: 'none',
                    '&.Mui-selected': {
                      color: '#1976d2',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.5,
                    },
                  },
                }}
              >
                {topTabsConfig.map((tab) => (
                  <Tooltip
                    key={tab.tabKind || tab.label}
                    title={
                      tab.enabled
                        ? ''
                        : tab.isNyscef
                          ? 'No NYSCEF intake files'
                          : tab.isFilings
                            ? 'No filing documents'
                            : 'No workup files generated'
                    }
                    disableHoverListener={tab.enabled}
                    placement="top"
                  >
                    <Tab label={tab.label} disabled={!tab.enabled} sx={{ minHeight: '36px' }} />
                  </Tooltip>
                ))}
              </Tabs>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <Tooltip title="Pull most recent documents" placement="bottom">
              <span>
                <IconButton
                  color="inherit"
                  onClick={handleRefreshCases}
                  disabled={refreshingCases || isFetching}
                  aria-label="pull most recent documents"
                >
                  {refreshingCases ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Open in window" placement="bottom">
              <span>
                <IconButton
                  color="inherit"
                  onClick={handleOpenInWindow}
                  disabled={!activePdf.url}
                  aria-label="open in window"
                >
                  <OpenInNewIcon />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      {showWorkupPickerBelow && (
        <DocumentPickerStrip ariaLabel="workup type">
          <WorkupVariantPickerRow
            variants={workupVariantsForPicker}
            workupVariantKind={workupVariantKind}
            onSelect={setWorkupVariantKind}
          />
        </DocumentPickerStrip>
      )}
      {showFilingsPicker && (
        <FilingsDocumentPicker
          filingsList={filingsList}
          selectedIndex={safeFilingsIdx}
          onSelect={setFilingsFileIndex}
        />
      )}
      {showNyscefPicker && (
        <NyscefExhibitPicker
          groups={nyscefByParty}
          selectedIndex={safeNyscefIdx}
          onSelect={setNyscefFileIndex}
        />
      )}
      <DialogContent sx={{ p: 0, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, width: '100%', minHeight: 0, position: 'relative' }}>
          {activePdf.url ? (
            <PdfViewer url={activePdf.url} title={activePdf.title} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography variant="body1" color="text.secondary">
                {activePdf.emptyMessage}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </StableMuiDialog>
  );
};

export default WorkupViewerDialog;
